<template>
  <div class="container-fluid">
    <div class="loading" v-if="isLoading">Loading&hellip;</div>

    <div class="view-header my-4">
      <div class="view-header__main">
        <div class="view-header__info">
          <h1 class="view-header__title">Descargas</h1>
          <p class="view-header__subtitle">Reportes y exportaciones generadas, listas para descargar.</p>
        </div>
      </div>
    </div>

    <div class="form-card">
      <div class="form-card__header">
        <h6 class="form-card__title">
          <i class="bi bi-cloud-arrow-down"></i>
          Listado de descargas
        </h6>
      </div>

      <div class="data-table downloads__table">
        <div class="table-responsive">
          <table class="table mb-0">
            <thead>
              <tr>
                <th>Fecha</th>
                <th>Archivo</th>
                <th>Estado</th>
                <th class="text-right">Acción</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="!downloadsList.length">
                <td colspan="4" class="data-table__empty">No tenés descargas registradas todavía.</td>
              </tr>
              <tr v-for="file in paginatedDownloads" :key="file.id">
                <td>{{ formatoFechaHora(file.created_at) }}</td>
                <td>
                  <span class="data-table__primary">
                    <i :class="isPDF(file.url) ? 'bi bi-filetype-pdf' : 'bi bi-filetype-csv'" class="mr-1"></i>
                    {{ getName(file.url) }}
                  </span>
                </td>
                <td>
                  <span
                    class="status-badge"
                    :class="file.status == 0 ? 'status-badge--success' : 'status-badge--neutral'"
                  >
                    {{ file.status == 0 ? 'Listo para descarga' : 'Descargado' }}
                  </span>
                </td>
                <td class="text-right">
                  <button
                    type="button"
                    class="table-action-btn"
                    :disabled="downloadingId === file.id"
                    :title="isPDF(file.url) ? 'Descargar PDF' : 'Descargar CSV'"
                    @click="downloadFile(file)"
                  >
                    <i :class="downloadingId === file.id ? 'bi bi-arrow-repeat downloads__spin' : 'bi bi-download'"></i>
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div v-if="downloadsList.length" class="data-table__footer">
          <div class="data-table__info">
            Mostrando {{ pageStart }}–{{ pageEnd }} de {{ downloadsList.length }}
          </div>
          <ul class="data-table__pagination">
            <li class="page-item" :class="{ disabled: currentPage === 1 }">
              <a href="#" class="page-link" @click.prevent="goToPage(currentPage - 1)">
                <i class="bi bi-chevron-left"></i>
              </a>
            </li>
            <li
              v-for="p in pageNumbers"
              :key="p"
              class="page-item"
              :class="{ active: p === currentPage }"
            >
              <a href="#" class="page-link" @click.prevent="goToPage(p)">{{ p }}</a>
            </li>
            <li class="page-item" :class="{ disabled: currentPage === totalPages }">
              <a href="#" class="page-link" @click.prevent="goToPage(currentPage + 1)">
                <i class="bi bi-chevron-right"></i>
              </a>
            </li>
          </ul>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import formatDateMixin from "../../mixins/formatDateMixin";
import exportsMixin from "../../mixins/exportsMixin";
import { mapState } from 'vuex';
import myaxios from 'axios';

export default {
  mixins: [formatDateMixin, exportsMixin],
  data: function () {
    return {
      currentPage: 1,
      pageSize: 10,
      downloadingId: null,
    };
  },
  computed: {
    ...mapState('downloads', {
      downloads: state => state.downloads,
      isLoading: state => state.loading,
    }),
    downloadsList() {
      // El store inicializa downloads como {} y lo reemplaza por el array al resolver.
      return Array.isArray(this.downloads) ? this.downloads : [];
    },
    totalPages() {
      return Math.max(1, Math.ceil(this.downloadsList.length / this.pageSize));
    },
    pageStart() {
      if (!this.downloadsList.length) return 0;
      return (this.currentPage - 1) * this.pageSize + 1;
    },
    pageEnd() {
      return Math.min(this.currentPage * this.pageSize, this.downloadsList.length);
    },
    paginatedDownloads() {
      const start = (this.currentPage - 1) * this.pageSize;
      return this.downloadsList.slice(start, start + this.pageSize);
    },
    pageNumbers() {
      const total = this.totalPages;
      const max = 5;
      let from = Math.max(1, this.currentPage - Math.floor(max / 2));
      let to = Math.min(total, from + max - 1);
      from = Math.max(1, to - max + 1);
      return Array.from({ length: to - from + 1 }, (_, i) => from + i);
    },
  },
  watch: {
    'downloadsList.length'() {
      this.currentPage = 1;
    },
  },
  mounted() {
    this.$store.dispatch('downloads/getDownloads');
  },
  methods: {
    isPDF(url) {
      return (url || '').toLowerCase().endsWith('.pdf');
    },
    getName(url) {
      if (!url) return '';
      return url.split('/').pop();
    },
    goToPage(p) {
      if (p < 1 || p > this.totalPages) return;
      this.currentPage = p;
    },
    async downloadFile(file) {
      if (this.downloadingId !== null) return;
      this.downloadingId = file.id;
      try {
        const isPdfFile = this.isPDF(file.url);
        const filename = this.getName(file.url) || (isPdfFile ? `descarga-${file.id}.pdf` : `descarga-${file.id}.csv`);

        const presignedResponse = await this.$http.post(`/api/downloads/getPresigned/${file.id}`);
        const fileURL = presignedResponse.data.data;

        // Instancia limpia: el axios global tiene interceptores que agregan el Authorization Bearer,
        // que rompe la URL presigneada de S3 si llega como header propio del request.
        const uninterceptedAxios = myaxios.create();
        const fileResponse = await uninterceptedAxios.get(fileURL, { responseType: 'blob' });

        const blobType = isPdfFile ? 'application/pdf' : 'text/csv';
        const blob = new Blob([fileResponse.data], { type: blobType });

        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = filename;
        link.click();
        URL.revokeObjectURL(link.href);

        // Refrescá el listado para que el estado pase a "Descargado".
        this.$store.dispatch('downloads/getDownloads');
      } catch (error) {
        console.error('Error downloading file:', error);
        alert('No se pudo descargar el archivo. Intentá de nuevo en unos minutos.');
      } finally {
        this.downloadingId = null;
      }
    },
  },
};
</script>

<style scoped>
.downloads__table {
  border: 0;
  border-radius: 0;
  box-shadow: none;
  margin-bottom: 0;
}

.downloads__spin {
  display: inline-block;
  animation: dl-spin 0.8s linear infinite;
}

@keyframes dl-spin {
  to { transform: rotate(360deg); }
}
</style>
